// DON'T TOUCH THIS
// These are separate clients and do not share configs between themselves
import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  from,
  InMemoryCache,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ENABLED_SERVICE_NAME_HEADER, getApiUrl } from "@dashboard/config";
import { createUploadLink } from "apollo-upload-client";

import { isJwtError } from "../auth/errors";
import { tokenRefresh } from "../auth/mutations";
import { authService } from "../auth/services/authService";
import { emitSessionExpired, resetSessionExpired } from "../auth/sessionExpiry";
import introspectionQueryResultData from "./fragmentTypes.generated";
import introspectionQueryResultDataStaging from "./fragmentTypesStaging.generated";
import { isStagingSchema } from "./schemaVersion";
import { TypedTypePolicies } from "./typePolicies.generated";

// Select the appropriate fragmentTypes and typePolicies based on schema version
const introspectionData = isStagingSchema()
  ? introspectionQueryResultDataStaging
  : introspectionQueryResultData;

let refreshTokenPromise: Promise<string | null> | null = null;

const createGraphQLError = (graphQLErrors: readonly any[] | undefined) =>
  new ApolloError({
    graphQLErrors: graphQLErrors ? [...graphQLErrors] : [],
  });

const requestNewAccessToken = async (): Promise<string | null> => {
  const refreshToken = authService.getRefreshToken();
  const csrfToken = authService.getCsrfToken();

  if (!refreshToken) {
    authService.clearTokens();
    emitSessionExpired();

    return null;
  }

  try {
    const response = await fetch(getApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: tokenRefresh.loc?.source.body,
        variables: { refreshToken, csrfToken },
      }),
    });
    const payload = await response.json();
    const newToken = payload.data?.tokenRefresh?.token;

    if (!newToken) {
      authService.clearTokens();
      emitSessionExpired();

      return null;
    }

    authService.setAccessToken(newToken);
    resetSessionExpired();

    return newToken;
  } catch {
    authService.clearTokens();
    emitSessionExpired();

    return null;
  }
};

const refreshAccessToken = () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = requestNewAccessToken().finally(() => {
      refreshTokenPromise = null;
    });
  }

  return refreshTokenPromise;
};

const attachVariablesLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => {
    const contextHeaders: Record<string, string> = { ...headers };

    if (ENABLED_SERVICE_NAME_HEADER) {
      contextHeaders["source-service-name"] = "saleor.dashboard";
    }

    return {
      headers: contextHeaders,
    };
  });

  return forward(operation).map(data => ({
    ...data,
    extensions: {
      ...data.extensions,
      variables: operation.variables,
    },
  }));
});

const authLink = setContext((_, { headers }) => {
  const token = authService.getAccessToken();

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

export const createAuthErrorLink = () =>
  onError(({ graphQLErrors, operation, forward }) => {
    if (!graphQLErrors?.some(isJwtError) || !forward) {
      return;
    }

    if (operation.getContext().authRefreshAttempted) {
      emitSessionExpired();

      return;
    }

    return new Observable(observer => {
      let subscription: ZenObservable.Subscription | undefined;
      let cancelled = false;

      void refreshAccessToken().then(newToken => {
        if (cancelled) {
          return;
        }

        if (!newToken) {
          observer.error(createGraphQLError(graphQLErrors));

          return;
        }

        operation.setContext(
          ({ authRefreshAttempted: _authRefreshAttempted, headers = {}, ...context }) => ({
            ...context,
            authRefreshAttempted: true,
            headers: {
              ...headers,
              Authorization: `Bearer ${newToken}`,
            },
          }),
        );

        subscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      });

      return () => {
        cancelled = true;
        subscription?.unsubscribe();
      };
    });
  });

const errorLink = createAuthErrorLink();

export const __testing__ = {
  createGraphQLError,
  refreshAccessToken,
  resetRefreshTokenPromise: () => {
    refreshTokenPromise = null;
  },
};

const uploadLink = createUploadLink({
  credentials: "include",
  uri: getApiUrl(),
}) as unknown as ApolloLink;

const link = from([errorLink, authLink, attachVariablesLink, uploadLink]);

export const apolloClient = new ApolloClient({
  connectToDevTools: process.env.NODE_ENV === "development",
  cache: new InMemoryCache({
    possibleTypes: introspectionData.possibleTypes,
    typePolicies: {
      CountryDisplay: {
        keyFields: ["code"],
      },
      Money: {
        merge: false,
      },
      TaxedMoney: {
        merge: false,
      },
      Weight: {
        merge: false,
      },
      Shop: {
        keyFields: [],
      },
      AttributeValue: {
        fields: {
          /**
           * Since, API sometimes creates an empty slug,
           * We need to handle that case also on front-end,
           * so after fix that problem in the API, the UI will ablle
           * to handle it.
           *
           * If the slug is empty, use the name
           */
          slug: (givenSlug, { readField }) => {
            if (!givenSlug) {
              return readField("name");
            }

            return givenSlug;
          },
        },
      },
      App: {
        keyFields: false,
      },
    } as TypedTypePolicies,
  }),
  link,
});
